import { useState } from 'react';
import { fetchWithCSRF } from '../utils/csrfUtils';
import { useAuth } from '../context/AuthContext';

const useGroceryListIngredientStatus = () => {
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState(null);
    const { sessionToken } = useAuth();

    const setGroceryListIngredientIsInCartStatus = async (groceryListIngredientId, isInCart, appUserId) => {
        setIsLoading(true);
        setError(null);
        try {
            if (!sessionToken) {
                throw new Error('No valid session found');
            }

            const response = await fetchWithCSRF(`/api/v1/grocery-list-ingredient/${groceryListIngredientId}/in-cart/app_user/${appUserId}`, {
                method: 'PUT',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${sessionToken}`
                },
                body: JSON.stringify({ is_in_cart: isInCart })
            });
            
            if (!response.ok) {
                const errorData = await response.json();
                throw new Error(errorData.error || 'Failed to update cart status');
            }

            const data = await response.json();
            setIsLoading(false);
            return data;   
        } catch (err) {
            const errorMessage = err.message || 'Failed to update cart status';
            setError(errorMessage);
            setIsLoading(false);
            throw err;
        }
    };

    const setGroceryListIngredientPurchasedStatus = async (groceryListIngredientId, isPurchased, appUserId) => {
        setIsLoading(true);
        setError(null);
        try {
            if (!sessionToken) {
                throw new Error('No valid session found');
            }

            const response = await fetchWithCSRF(`/api/v1/grocery-list-ingredient/${groceryListIngredientId}/purchased/app_user/${appUserId}`, {
                method: 'PUT',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${sessionToken}`
                },
                body: JSON.stringify({ is_purchased: isPurchased })
            });

            if (!response.ok) {
                const errorData = await response.json();
                throw new Error(errorData.error || 'Failed to update purchase status');
            }

            const data = await response.json();
            setIsLoading(false);
            return data;
        } catch (err) {
            const errorMessage = err.message || 'Failed to update purchase status';
            setError(errorMessage);
            setIsLoading(false);
            throw err;
        }
    };


    const setMultipleGroceryListIngredientsPurchasedStatus = async (groceryListIngredientIds, isPurchased, appUserId) => {
        setIsLoading(true);
        setError(null);
        try {
            if (!sessionToken) {
                throw new Error('No valid session found');
            }
    
            const response = await fetchWithCSRF(`/api/v1/grocery-list-ingredient/multiple/app_user/${appUserId}`, {
                method: 'PUT',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${sessionToken}`
                },
                body: JSON.stringify({ 
                    ids: groceryListIngredientIds,
                    is_purchased: isPurchased 
                })
            });
    
            if (!response.ok) {
                const errorData = await response.json();
                throw new Error(errorData.error || 'Failed to update purchase status for multiple items');
            }
    
            const data = await response.json();
            setIsLoading(false);
            return data;
        } catch (err) {
            const errorMessage = err.message || 'Failed to update purchase status for multiple items';
            setError(errorMessage);
            setIsLoading(false);
            throw err;
        }
    };

    const updateGroceryListIngredientAdjustedQuantity = async (groceryListIngredientId, adjustedQuantity, appUserId) => {
        setIsLoading(true);
        setError(null);
        try {
            if (!sessionToken) {
                throw new Error('No valid session found');
            }
            const response = await fetchWithCSRF(`/api/v1/grocery-list-ingredient/${groceryListIngredientId}/adjusted_quantity/app_user/${appUserId}`, {
                method: 'PUT',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${sessionToken}`
                },
                body: JSON.stringify({ adjusted_quantity: adjustedQuantity })
            });
            if (!response.ok) {
                const errorData = await response.json();
                throw new Error(errorData.error || 'Failed to update adjusted quantity');
            }
            const data = await response.json();
            setIsLoading(false);
            return data;
        } catch (err) {
            const errorMessage = err.message || 'Failed to update adjusted quantity';
            setError(errorMessage);
            setIsLoading(false);
            throw err;
        }
    };

    return { 
        setGroceryListIngredientIsInCartStatus, 
        setGroceryListIngredientPurchasedStatus, 
        setMultipleGroceryListIngredientsPurchasedStatus,
        updateGroceryListIngredientAdjustedQuantity,
        isLoading, 
        error 
    };
};

export default useGroceryListIngredientStatus;